import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  EmailBuilderBlock,
  UpdateEmailBuilderState,
  emailBuilderBlockLabels,
} from "./emailBuilderData";
import { moveEmailBuilderBlock } from "./emailBuilderReorder";

export const emailBuilderBlockReorderInstructionsId =
  "email-builder-block-reorder-instructions";

type KeyboardBlockReorderSession = {
  blockId: string;
  originalIndex: number;
};

type PointerBlockReorderSession = KeyboardBlockReorderSession & {
  pointerId: number;
  handle: HTMLButtonElement;
  startX: number;
  startY: number;
  currentIndex: number;
  hasMoved: boolean;
  lastTargetBlockId: string;
};

export type BlockReorderAnnouncement = {
  sequence: number;
  message: string;
};

export type EmailBuilderBlockReorderBindings = {
  pointerDragging: boolean;
  pointerDropTarget: boolean;
  keyboardGrabbed: boolean;
  onMove: (direction: -1 | 1) => void;
  setMoveHandleRef: (element: HTMLButtonElement | null) => void;
  onPointerReorderStart: (
    event: React.PointerEvent<HTMLButtonElement>
  ) => void;
  onPointerReorderMove: (
    event: React.PointerEvent<HTMLButtonElement>
  ) => void;
  onPointerReorderEnd: (
    event: React.PointerEvent<HTMLButtonElement>
  ) => void;
  onPointerReorderCancel: (
    event: React.PointerEvent<HTMLButtonElement>
  ) => void;
  onKeyboardReorder: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onKeyboardReorderBlur: () => void;
};

export type EmailBuilderBlockReorderController = {
  announcement: BlockReorderAnnouncement;
  getBlockBindings: (input: {
    blockId: string;
  }) => EmailBuilderBlockReorderBindings;
};

type UseEmailBuilderBlockReorderInput = {
  blocks: EmailBuilderBlock[];
  disabled: boolean;
  onSelectBlock: (input: { blockId: string }) => void;
  selectedBlockId: string;
  updateBuilderState: UpdateEmailBuilderState;
  onHistoryTransactionStart?: () => void;
  onHistoryTransactionEnd?: (cancel?: boolean) => void;
};

type BlockContext = {
  count: number;
  index: number;
  label: string;
};

const pointerReorderActivationDistance = 6;

const scrollBlockDragViewport = ({
  handle,
  clientY,
}: {
  handle: HTMLButtonElement;
  clientY: number;
}) => {
  // EC-17: the builder page scrolls as one (dashboard scroll area); the
  // canvas only scrolls on its own in older layouts.
  const scrollContainer =
    handle.closest("[data-dashboard-scroll], .flex-1.overflow-auto") ||
    handle.closest(".cmp-email-builder-canvas");
  if (!(scrollContainer instanceof HTMLElement)) return;

  const bounds = scrollContainer.getBoundingClientRect();
  const edgeSize = 72;
  const maxScrollStep = 18;
  let scrollStep = 0;

  if (clientY < bounds.top + edgeSize) {
    scrollStep = -Math.min(
      maxScrollStep,
      Math.ceil((bounds.top + edgeSize - clientY) / 4)
    );
  } else if (clientY > bounds.bottom - edgeSize) {
    scrollStep = Math.min(
      maxScrollStep,
      Math.ceil((clientY - (bounds.bottom - edgeSize)) / 4)
    );
  }

  if (scrollStep !== 0) {
    scrollContainer.scrollBy({ top: scrollStep, behavior: "auto" });
  }
};

export const useEmailBuilderBlockReorder = ({
  blocks,
  disabled,
  onSelectBlock,
  selectedBlockId,
  updateBuilderState,
  onHistoryTransactionStart, onHistoryTransactionEnd,
}: UseEmailBuilderBlockReorderInput): EmailBuilderBlockReorderController => {
  const [keyboardReorder, setKeyboardReorder] =
    useState<KeyboardBlockReorderSession | null>(null);
  const [pointerDraggingBlockId, setPointerDraggingBlockId] = useState("");
  const [pointerDropTargetBlockId, setPointerDropTargetBlockId] = useState("");
  const [announcement, setAnnouncement] = useState<BlockReorderAnnouncement>({
    sequence: 0,
    message: "",
  });
  const moveHandleRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const pointerSessionRef = useRef<PointerBlockReorderSession | null>(null);
  const keyboardSessionRef = useRef<KeyboardBlockReorderSession | null>(null);

  const announce = useCallback((message: string) =>
    setAnnouncement((currentAnnouncement) => ({
      sequence: currentAnnouncement.sequence + 1,
      message,
    })), []);

  const getBlockContext = useCallback(
    ({ blockId }: { blockId: string }): BlockContext => {
      const index = blocks.findIndex((block) => block.id === blockId);
      const block = blocks[index];

      return {
        count: blocks.length,
        index,
        label: block ? emailBuilderBlockLabels[block.type] : "Email content",
      };
    },
    [blocks]
  );

  const moveBlockToIndex = useCallback(
    ({
      blockId,
      targetIndex,
    }: {
      blockId: string;
      targetIndex: number;
    }) =>
      updateBuilderState((currentState) => {
        const nextBlocks = moveEmailBuilderBlock({
          blocks: currentState.blocks,
          blockId,
          targetIndex,
        });

        return nextBlocks === currentState.blocks
          ? currentState
          : { ...currentState, selectedBlockId: blockId, blocks: nextBlocks };
      }),
    [updateBuilderState]
  );

  const moveBlock = ({
    blockId,
    direction,
  }: {
    blockId: string;
    direction: -1 | 1;
  }) => {
    const context = getBlockContext({ blockId });
    const targetIndex = context.index + direction;
    if (
      context.index < 0 ||
      targetIndex < 0 ||
      targetIndex >= context.count
    ) {
      return;
    }

    moveBlockToIndex({ blockId, targetIndex });
    announce(
      `Moved ${context.label} block to position ${targetIndex + 1} of ${
        context.count
      }.`
    );
  };

  const finishPointerReorder = (result: "drop" | "cancel" | "unmount") => {
    const session = pointerSessionRef.current;
    if (!session) return;

    // Release can dispatch lostpointercapture. Clear the session before that event.
    pointerSessionRef.current = null;
    if (session.handle.hasPointerCapture(session.pointerId)) {
      session.handle.releasePointerCapture(session.pointerId);
    }
    if (result !== "unmount") {
      setPointerDraggingBlockId("");
      setPointerDropTargetBlockId("");
    }
    if (!session.hasMoved) return;

    const cancel = result !== "drop";
    if (cancel) {
      moveBlockToIndex({
        blockId: session.blockId,
        targetIndex: session.originalIndex,
      });
    }
    if (result !== "unmount") {
      const context = getBlockContext({ blockId: session.blockId });
      announce(cancel
        ? `Canceled moving ${context.label} block. It returned to position ${
            session.originalIndex + 1
          } of ${context.count}.`
        : `Dropped ${context.label} block at position ${
            session.currentIndex + 1
          } of ${context.count}.`
      );
    }
    if (cancel) onHistoryTransactionEnd?.(true);
    else onHistoryTransactionEnd?.();
  };

  const handlePointerReorderStart = ({
    blockId,
    event,
  }: {
    blockId: string;
    event: React.PointerEvent<HTMLButtonElement>;
  }) => {
    if (
      disabled || !event.isPrimary || event.button !== 0 || pointerSessionRef.current
    ) return;

    const context = getBlockContext({ blockId });
    if (context.index < 0 || context.count < 2) return;

    if (keyboardSessionRef.current) {
      keyboardSessionRef.current = null;
      setKeyboardReorder(null);
      onHistoryTransactionEnd?.();
    }

    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerSessionRef.current = {
      blockId,
      pointerId: event.pointerId,
      handle: event.currentTarget,
      startX: event.clientX,
      startY: event.clientY,
      originalIndex: context.index,
      currentIndex: context.index,
      hasMoved: false,
      lastTargetBlockId: "",
    };
    onSelectBlock({ blockId });
  };

  const handlePointerReorderMove = ({
    blockId,
    event,
  }: {
    blockId: string;
    event: Pick<PointerEvent, "pointerId" | "clientX" | "clientY" | "preventDefault">;
  }) => {
    const session = pointerSessionRef.current;
    if (
      !session ||
      session.blockId !== blockId ||
      session.pointerId !== event.pointerId
    ) {
      return;
    }

    const distance = Math.hypot(
      event.clientX - session.startX,
      event.clientY - session.startY
    );
    if (!session.hasMoved && distance < pointerReorderActivationDistance) return;

    event.preventDefault();
    const context = getBlockContext({ blockId });
    if (!session.hasMoved) {
      onHistoryTransactionStart?.();
      session.hasMoved = true;
      setPointerDraggingBlockId(blockId);
      announce(
        `Picked up ${context.label} block at position ${
          session.originalIndex + 1
        } of ${context.count}.`
      );
    }

    scrollBlockDragViewport({
      handle: moveHandleRefs.current.get(blockId) || session.handle,
      clientY: event.clientY,
    });

    const hitElement = document.elementFromPoint(event.clientX, event.clientY);
    const targetBlockElement = hitElement?.closest<HTMLElement>(
      "[data-email-builder-block-id]"
    );
    const targetBlockId = targetBlockElement?.dataset.emailBuilderBlockId || "";

    if (!targetBlockId || targetBlockId === blockId) {
      session.lastTargetBlockId = "";
      setPointerDropTargetBlockId("");
      return;
    }
    if (targetBlockId === session.lastTargetBlockId) return;

    const targetContext = getBlockContext({ blockId: targetBlockId });
    if (targetContext.index < 0) return;

    session.currentIndex = targetContext.index;
    session.lastTargetBlockId = targetBlockId;
    setPointerDropTargetBlockId(targetBlockId);
    moveBlockToIndex({ blockId, targetIndex: targetContext.index });
    announce(
      `Moved ${context.label} block to position ${targetContext.index + 1} of ${
        context.count
      }.`
    );
  };

  const handlePointerReorderEnd = ({
    blockId,
    event,
  }: {
    blockId: string;
    event: React.PointerEvent<HTMLButtonElement>;
  }) => {
    const session = pointerSessionRef.current;
    if (
      !session ||
      session.blockId !== blockId ||
      session.pointerId !== event.pointerId
    ) {
      return;
    }

    finishPointerReorder("drop");
  };

  const handlePointerReorderCancel = ({
    blockId,
    event,
  }: {
    blockId: string;
    event: React.PointerEvent<HTMLButtonElement>;
  }) => {
    const session = pointerSessionRef.current;
    if (
      session?.blockId === blockId && session.pointerId === event.pointerId
    ) finishPointerReorder("cancel");
  };

  const handleKeyboardReorder = ({
    blockId,
    event,
  }: {
    blockId: string;
    event: React.KeyboardEvent<HTMLButtonElement>;
  }) => {
    if (disabled || pointerSessionRef.current) return;
    const context = getBlockContext({ blockId });
    const session = keyboardSessionRef.current;
    const isGrabbed = session?.blockId === blockId;

    if (!isGrabbed) {
      if (event.key !== " " && event.key !== "Enter") return;

      event.preventDefault();
      event.stopPropagation();
      if (context.index < 0 || context.count < 2) return;

      onHistoryTransactionStart?.();
      onSelectBlock({ blockId });
      keyboardSessionRef.current = { blockId, originalIndex: context.index };
      setKeyboardReorder(keyboardSessionRef.current);
      announce(
        `Picked up ${context.label} block at position ${context.index + 1} of ${
          context.count
        }. Use Arrow Up and Arrow Down to move it, Space or Enter to drop it, or Escape to cancel.`
      );
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      const targetIndex =
        context.index + (event.key === "ArrowUp" ? -1 : 1);
      if (targetIndex < 0 || targetIndex >= context.count) {
        announce(
          `${context.label} block is already at the ${
            event.key === "ArrowUp" ? "top" : "bottom"
          }.`
        );
        return;
      }

      moveBlockToIndex({ blockId, targetIndex });
      announce(
        `Moved ${context.label} block to position ${targetIndex + 1} of ${
          context.count
        }.`
      );
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      moveBlockToIndex({
        blockId,
        targetIndex: session.originalIndex,
      });
      keyboardSessionRef.current = null;
      setKeyboardReorder(null);
      onHistoryTransactionEnd?.(true);
      announce(
        `Canceled moving ${context.label} block. It returned to position ${
          session.originalIndex + 1
        } of ${context.count}.`
      );
      return;
    }

    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      keyboardSessionRef.current = null;
      setKeyboardReorder(null);
      onHistoryTransactionEnd?.();
      announce(
        `Dropped ${context.label} block at position ${context.index + 1} of ${
          context.count
        }.`
      );
    }
  };

  const cancelKeyboardReorder = useCallback(
    ({ blockId, unmounting = false }: { blockId: string; unmounting?: boolean }) => {
      const session = keyboardSessionRef.current;
      if (session?.blockId !== blockId) return;

      keyboardSessionRef.current = null;
      moveBlockToIndex({ blockId, targetIndex: session.originalIndex });
      if (!unmounting) {
        const context = getBlockContext({ blockId });
        setKeyboardReorder(null);
        announce(
          `Canceled moving ${context.label} block. It returned to position ${
            session.originalIndex + 1
          } of ${context.count}.`
        );
      }
      onHistoryTransactionEnd?.(true);
    },
    [announce, getBlockContext, moveBlockToIndex, onHistoryTransactionEnd]
  );

  const lifecycleRef = useRef({
    movePointer: handlePointerReorderMove,
    finishPointer: finishPointerReorder,
    cancelKeyboard: cancelKeyboardReorder,
  });
  lifecycleRef.current = {
    movePointer: handlePointerReorderMove,
    finishPointer: finishPointerReorder,
    cancelKeyboard: cancelKeyboardReorder,
  };

  useEffect(() => {
    const isActivePointer = (event: PointerEvent) =>
      pointerSessionRef.current?.pointerId === event.pointerId;
    const movePointer = (event: PointerEvent) => {
      const session = pointerSessionRef.current;
      if (!session || !isActivePointer(event)) return;
      // React handles events on the button. Global tracking also survives DOM moves.
      const handle = moveHandleRefs.current.get(session.blockId) || session.handle;
      if (event.target instanceof Node && handle.contains(event.target)) return;
      if (event.buttons === 0) {
        lifecycleRef.current.finishPointer("drop");
        return;
      }
      lifecycleRef.current.movePointer({ blockId: session.blockId, event });
    };
    const endPointer = (event: PointerEvent) => {
      if (isActivePointer(event)) lifecycleRef.current.finishPointer("drop");
    };
    const cancelPointer = (event: PointerEvent) => {
      if (isActivePointer(event)) lifecycleRef.current.finishPointer("cancel");
    };
    const loseCapture = (event: PointerEvent) => {
      // A DOM move can release capture while the pointer is still pressed.
      // Keep tracking that pointer globally until it is released or canceled.
      if (isActivePointer(event) && event.buttons === 0)
        lifecycleRef.current.finishPointer("drop");
    };
    const cancelOnBlur = () => {
      lifecycleRef.current.finishPointer("cancel");
      const keyboard = keyboardSessionRef.current;
      if (keyboard) lifecycleRef.current.cancelKeyboard({ blockId: keyboard.blockId });
    };
    window.addEventListener("pointermove", movePointer, true);
    window.addEventListener("pointerup", endPointer, true);
    window.addEventListener("pointercancel", cancelPointer, true);
    window.addEventListener("lostpointercapture", loseCapture, true);
    window.addEventListener("blur", cancelOnBlur);
    return () => {
      window.removeEventListener("pointermove", movePointer, true);
      window.removeEventListener("pointerup", endPointer, true);
      window.removeEventListener("pointercancel", cancelPointer, true);
      window.removeEventListener("lostpointercapture", loseCapture, true);
      window.removeEventListener("blur", cancelOnBlur);
      lifecycleRef.current.finishPointer("unmount");
      const keyboard = keyboardSessionRef.current;
      if (keyboard) lifecycleRef.current.cancelKeyboard({
        blockId: keyboard.blockId, unmounting: true,
      });
    };
  }, []);

  useEffect(() => {
    if (
      keyboardReorder &&
      (disabled || selectedBlockId !== keyboardReorder.blockId)
    ) {
      cancelKeyboardReorder({ blockId: keyboardReorder.blockId });
    }
  }, [cancelKeyboardReorder, disabled, keyboardReorder, selectedBlockId]);

  useEffect(() => {
    if (!keyboardReorder) return;

    const moveHandle = moveHandleRefs.current.get(keyboardReorder.blockId);
    if (moveHandle && document.activeElement !== moveHandle) {
      moveHandle.focus({ preventScroll: true });
    }
  }, [blocks, keyboardReorder]);

  const getBlockBindings = ({
    blockId,
  }: {
    blockId: string;
  }): EmailBuilderBlockReorderBindings => ({
    pointerDragging: pointerDraggingBlockId === blockId,
    pointerDropTarget: pointerDropTargetBlockId === blockId,
    keyboardGrabbed: keyboardReorder?.blockId === blockId,
    onMove: (direction) => moveBlock({ blockId, direction }),
    setMoveHandleRef: (element) => {
      if (element) {
        moveHandleRefs.current.set(blockId, element);
      } else {
        moveHandleRefs.current.delete(blockId);
      }
    },
    onPointerReorderStart: (event) =>
      handlePointerReorderStart({ blockId, event }),
    onPointerReorderMove: (event) =>
      handlePointerReorderMove({ blockId, event }),
    onPointerReorderEnd: (event) =>
      handlePointerReorderEnd({ blockId, event }),
    onPointerReorderCancel: (event) =>
      handlePointerReorderCancel({ blockId, event }),
    onKeyboardReorder: (event) => handleKeyboardReorder({ blockId, event }),
    onKeyboardReorderBlur: () => cancelKeyboardReorder({ blockId }),
  });

  return { announcement, getBlockBindings };
};
